import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../../config/ApiUrl";

const WM = API_URL.WHATSAPP_MONITORING;
const WS = API_URL.WHATSAPP_SUPPORT;

// Admin (SuperAdmin) read-only WhatsApp monitoring.

// View A — cross-tenant fleet overview (KPI tiles + per-tenant rows).
export const getWhatsAppOverview = createAsyncThunk(
  "whatsAppMonitoring/getOverview",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(WM.OVERVIEW);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// View B — per-tenant drill-down (connection, phones, templates, metrics, inbound).
export const getTenantWhatsAppDetail = createAsyncThunk(
  "whatsAppMonitoring/getTenantDetail",
  async (tenantId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(WM.TENANT_DETAIL(tenantId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// View B — per-tenant paged message log (masked).
export const getTenantWhatsAppMessages = createAsyncThunk(
  "whatsAppMonitoring/getTenantMessages",
  async ({ tenantId, filter = {}, paging = {} }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(WM.TENANT_MESSAGES(tenantId), {
        params: { ...filter, ...paging },
      });
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// --- Admin support actions (write). Callers .unwrap() then refetch the tenant detail. ---

export const testTenantConnection = createAsyncThunk(
  "whatsAppMonitoring/testConnection",
  async (tenantId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(WS.TEST_CONNECTION(tenantId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const syncTenantTemplates = createAsyncThunk(
  "whatsAppMonitoring/syncTemplates",
  async (tenantId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(WS.SYNC_TEMPLATES(tenantId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const disconnectTenant = createAsyncThunk(
  "whatsAppMonitoring/disconnect",
  async ({ tenantId, reason }, { rejectWithValue }) => {
    try {
      await axios.post(WS.DISCONNECT(tenantId), { reason: reason || null });
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const connectTenantManual = createAsyncThunk(
  "whatsAppMonitoring/connectManual",
  async ({ tenantId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(WS.CONNECT_MANUAL(tenantId), payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const whatsAppMonitoringSlice = createSlice({
  name: "whatsAppMonitoring",
  initialState: {
    overview: null,
    detail: null,
    messages: null,
    isLoading: false,
    isDetailLoading: false,
    isMessagesLoading: false,
  },
  reducers: {
    clearWhatsAppMonitoring: (state) => {
      state.detail = null;
      state.messages = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWhatsAppOverview.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getWhatsAppOverview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.overview = action.payload || null;
      })
      .addCase(getWhatsAppOverview.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getTenantWhatsAppDetail.pending, (state) => {
        state.isDetailLoading = true;
      })
      .addCase(getTenantWhatsAppDetail.fulfilled, (state, action) => {
        state.isDetailLoading = false;
        state.detail = action.payload || null;
      })
      .addCase(getTenantWhatsAppDetail.rejected, (state) => {
        state.isDetailLoading = false;
      })
      .addCase(getTenantWhatsAppMessages.pending, (state) => {
        state.isMessagesLoading = true;
      })
      .addCase(getTenantWhatsAppMessages.fulfilled, (state, action) => {
        state.isMessagesLoading = false;
        state.messages = action.payload || null;
      })
      .addCase(getTenantWhatsAppMessages.rejected, (state) => {
        state.isMessagesLoading = false;
      });
  },
});

export const { clearWhatsAppMonitoring } = whatsAppMonitoringSlice.actions;
export default whatsAppMonitoringSlice.reducer;
